import { create } from 'zustand';
import { startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { useEffect, useRef, useCallback } from 'react';
import { useUrlValue } from './urlStateStore';
import type { PostStatus } from '../components/common/PostStatusDropdown';
import { useSearchParams } from 'react-router-dom';

// Define valid view types for Content Hub
type ContentHubView = 'list' | 'grid' | 'board';

// Content Hub page state
interface ContentHubState {
  dateRange: 'this-month' | 'last-month' | number | 'custom';
  customDateRange: {
    start: Date;
    end: Date;
  };
  selectedBrand: string;
  view: ContentHubView; // Use the specific type
  statusFilter: PostStatus;
  
  // Actions
  setDateRange: (dateRange: 'this-month' | 'last-month' | number | 'custom') => void;
  setCustomDateRange: (range: { start: Date; end: Date }) => void;
  setSelectedBrand: (brandId: string) => void;
  setView: (view: ContentHubView) => void; // Use the specific type
  setStatusFilter: (status: PostStatus) => void;
}

// Create the store with default values for Content Hub
export const useContentHubStore = create<ContentHubState>((set, get) => {
  const lastMonth = subMonths(new Date(), 1);
  
  return {
    dateRange: 'this-month',
    customDateRange: {
      start: startOfMonth(lastMonth),
      end: endOfMonth(lastMonth)
    },
    selectedBrand: 'all',
    view: 'list', // Default to 'list' view
    statusFilter: 'all',
    
    // Actions
    setDateRange: (dateRange) => set((state) => {
      if (state.dateRange === dateRange) return state;
      return { dateRange };
    }),
    setCustomDateRange: (customDateRange) => set({ customDateRange }),
    setSelectedBrand: (selectedBrand) => set((state) => {
      if (state.selectedBrand === selectedBrand) return state;
      return { selectedBrand };
    }),
    setView: (view) => set((state) => {
      if (state.view === view) return state;
      return { view };
    }),
    setStatusFilter: (statusFilter) => set((state) => {
      if (state.statusFilter === statusFilter) return state;
      return { statusFilter };
    }),
  };
});

// Hook to use Content Hub state with URL synchronization
export function useContentHubUrlState() {
  // Get the raw state and actions from Zustand
  const store = useContentHubStore();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Create refs to track initial values and prevent circular updates
  const isInitialMount = useRef(true);
  const updatingFromStore = useRef(false);
  const storeRef = useRef(store);
  
  // Update store ref
  useEffect(() => {
    storeRef.current = store;
  }, [store]);
  
  // Clean up URL by removing unwanted parameters (optional, can customize if needed)
  useEffect(() => {
    let shouldUpdateParams = false;
    const newParams = new URLSearchParams(searchParams);
    
    // Example: Remove parameters specific to other pages if they appear
    if (newParams.has('platform')) { // From Analytics
      newParams.delete('platform');
      shouldUpdateParams = true;
    }
    if (newParams.has('tab')) { // From Accounts
      newParams.delete('tab');
      shouldUpdateParams = true;
    }
    
    if (shouldUpdateParams) {
      setSearchParams(newParams);
    }
  }, [searchParams, setSearchParams]);
  
  // Connect state values to URL parameters with stable options
  const viewOptions = useRef({ skipSync: false }).current;
  const statusOptions = useRef({ skipSync: false }).current;
  const brandOptions = useRef({ skipSync: false }).current;
  const dateRangeOptions = useRef({ skipSync: false }).current;
  
  // Connect state values to URL parameters
  const [view, setView] = useUrlValue<ContentHubView>( // Use the specific type
    'view', 
    store.view,
    viewOptions
  );
  
  const [statusFilter, setStatusFilter] = useUrlValue<PostStatus>(
    'status',
    store.statusFilter,
    statusOptions
  );
  
  const [selectedBrand, setSelectedBrand] = useUrlValue<string>(
    'brand',
    store.selectedBrand,
    brandOptions
  );
  
  const [dateRange, setDateRange] = useUrlValue<'this-month' | 'last-month' | number | 'custom'>(
    'dateRange',
    store.dateRange,
    dateRangeOptions
  );
  
  // On initial mount, sync the store with URL values (URL has priority)
  useEffect(() => {
    if (isInitialMount.current) {
      const currentStore = storeRef.current;
      
      // Use the correct type for view comparison
      if (view !== currentStore.view) currentStore.setView(view);
      if (statusFilter !== currentStore.statusFilter) currentStore.setStatusFilter(statusFilter);
      if (selectedBrand !== currentStore.selectedBrand) currentStore.setSelectedBrand(selectedBrand);
      if (dateRange !== currentStore.dateRange) currentStore.setDateRange(dateRange);
      
      isInitialMount.current = false;
    }
  }, []); // Empty dependency array ensures this runs only once
  
  // Create wrapper functions for setters that update both URL and store
  const setViewWrapper = useCallback((newView: ContentHubView) => { // Use the specific type
    if (newView === view) return;
    
    setView(newView);
    updatingFromStore.current = true;
    storeRef.current.setView(newView);
    updatingFromStore.current = false;
  }, [view, setView]);
  
  const setStatusFilterWrapper = useCallback((newStatus: PostStatus) => {
    if (newStatus === statusFilter) return;
    
    setStatusFilter(newStatus);
    updatingFromStore.current = true;
    storeRef.current.setStatusFilter(newStatus);
    updatingFromStore.current = false;
  }, [statusFilter, setStatusFilter]);
  
  const setSelectedBrandWrapper = useCallback((newBrand: string) => {
    if (newBrand === selectedBrand) return;
    
    setSelectedBrand(newBrand);
    updatingFromStore.current = true;
    storeRef.current.setSelectedBrand(newBrand);
    updatingFromStore.current = false;
  }, [selectedBrand, setSelectedBrand]);
  
  const setDateRangeWrapper = useCallback((newDateRange: 'this-month' | 'last-month' | number | 'custom') => {
    if (newDateRange === dateRange) return;
    
    setDateRange(newDateRange);
    updatingFromStore.current = true;
    storeRef.current.setDateRange(newDateRange);
    updatingFromStore.current = false;
  }, [dateRange, setDateRange]);
  
  // Keep Zustand store in sync with URL changes
  useEffect(() => {
    if (!isInitialMount.current && !updatingFromStore.current && view !== storeRef.current.view) {
      storeRef.current.setView(view);
    }
  }, [view]);
  
  useEffect(() => {
    if (!isInitialMount.current && !updatingFromStore.current && statusFilter !== storeRef.current.statusFilter) {
      storeRef.current.setStatusFilter(statusFilter);
    }
  }, [statusFilter]);
  
  useEffect(() => {
    if (!isInitialMount.current && !updatingFromStore.current && selectedBrand !== storeRef.current.selectedBrand) {
      storeRef.current.setSelectedBrand(selectedBrand);
    }
  }, [selectedBrand]);
  
  useEffect(() => {
    if (!isInitialMount.current && !updatingFromStore.current && dateRange !== storeRef.current.dateRange) {
      storeRef.current.setDateRange(dateRange);
    }
  }, [dateRange]);
  
  // Return URL-synced values and our wrapped setters
  return {
    // URL synced values
    view,
    statusFilter,
    selectedBrand,
    dateRange,
    
    // URL synced setters (using our wrapper functions)
    setView: setViewWrapper,
    setStatusFilter: setStatusFilterWrapper,
    setSelectedBrand: setSelectedBrandWrapper,
    setDateRange: setDateRangeWrapper,
    
    // Values not synced with URL (can add back if needed, e.g., customDateRange)
    customDateRange: store.customDateRange, 
    
    // Setters not synced with URL (can add back if needed)
    setCustomDateRange: store.setCustomDateRange,
  };
} 